import {computed, defineComponent, ref, watch} from 'vue';
import {useForm} from "vee-validate";
import {
    OrganisationInvoiceGroup,
    OrganisationInvoiceGroupMember,
    SubOrganisation
} from "@/api/klip-api.proxy";
import {useKlModalEventsUtil} from "@/app/shared/helpers/kl-modal-events-util";
import * as yup from 'yup';
import {toTypedSchema} from '@vee-validate/yup';
import {
    klValidationFields,
    ValidationFieldsType
} from '@/app/shared/components/kl-form-fields/kl-field-validation-util';

// Extend the SubOrganisation interface
interface OrganisationForInvoiceGroup extends SubOrganisation {
    type: string;
    checked: boolean;
}

export default defineComponent({
    compatConfig: {
        MODE: 3,
    },
    name: 'KlInvoiceGroupEdit',
    props: {
        disabledOrganisations: {
            type: Object as () => OrganisationInvoiceGroupMember[],
            default: () => ({}),
            required: true
        },
        disableCreateButton: {
            type: Boolean,
            default: false
        },
        subOrganisations: {
            type: Array as () => SubOrganisation[],
            default: () => [],
            required: true
        },
        invoiceGroup: {
            type: Object as () => OrganisationInvoiceGroup,
            default: () => ({}),
            required: true
        },
        showContactInfo: {
            type: Boolean,
            default: false,
            required: false
        }
    },
    emits: ['onSave', 'onCancel'],
    setup(props,  { emit }) {

        const schema = yup.object({
            name: yup.string().notRequired().max(200).label('Naam facturatiegroep'),
            email: yup.string().required().email().max(200).label('E-mail'),
            attention: yup.string().notRequired().max(200).label('Ter attentie van'),
            reference: yup.string().notRequired().max(25).label('Eigen referentie'),
            invoiceContactName: yup.string().notRequired().max(200).label('Naam'),
            invoiceContactEmail: yup.string().notRequired().email().max(320).label('Contactpersoon e-mail'),
            invoiceContactPhoneNumber: yup.string().notRequired().kl_phone().max(200).label('Telefoon'),
            organisations: yup.array().required().min(1),
        });

        const { meta, handleSubmit , resetForm } = useForm({
            validationSchema: toTypedSchema(schema),
        });
        const fields: ValidationFieldsType<typeof schema> = klValidationFields<typeof schema>(schema);

        const name = fields.name.getValue<string>();
        const email = fields.email.getValue<string>();
        const attention = fields.attention.getValue<string>();
        const reference = fields.reference.getValue<string>();
        const invoiceContactName = fields.invoiceContactName.getValue<string>();
        const invoiceContactEmail = fields.invoiceContactEmail.getValue<string>();
        const invoiceContactPhoneNumber = fields.invoiceContactPhoneNumber.getValue<string>();
        const organisations = fields.organisations.getValue<OrganisationInvoiceGroupMember[]>();

        const isSending = ref<boolean>(false);
        const isOrganisationsDataTableLoading = ref<boolean>(false);
        const disableSubmit = computed((): boolean => {
            return !meta.value.valid || !meta.value.dirty || isSending.value;
        });
        const disableCreate = ref<boolean>(false);

        const allOrganisations = ref<OrganisationForInvoiceGroup[]>([]);

        const createInvoiceGroupModalEvents = useKlModalEventsUtil('create-invoice-group-modal', {
            onClosed() {
                isSending.value = false;
                resetForm();
                emit('onCancel');
            }
        });

        const organisationsMeta = computed((): any => {
            return {
                totalRows: props.subOrganisations.length ?? 0,
                resultsPerPage: props.subOrganisations.length ?? 0,
                currentPage: 0
            };
        });

        const isEditMode = computed(() => {return !!props.invoiceGroup?.id});

        const title = computed((): string => {
            return isEditMode.value ? 'Bewerk facturatiegroep' : 'Nieuwe facturatiegroep';
        });

        const submitButtonLabel = computed((): string => {
            return isEditMode.value ? 'Bewaar veranderingen' : 'Maak nieuwe facturatiegroep';
        });

        const cellParser = (row: any, column: IColumn) => {
            switch (column.key) {
                case 'name':
                    const depth = row['depth'] as number;
                    const marginLeft = `margin-left: ${depth * 20}px;`;
                    return {
                        template: `<div style='${marginLeft}'>${row[column.key]}</div>`,
                    };
            }
        };

        const organisationsColumns = ref<IColumn[]>([
            {
                key: 'name',
                label: 'Organisatie',
                sortable: false,
                isVisible: true,
                width: 95,
                parser: cellParser
            }
        ]);

        const cancel = () => {
            createInvoiceGroupModalEvents.triggerModalToggle();
            resetForm();
        }

        const checkboxChanged = (checkedRows: SubOrganisation[]) => {
            organisations.value = checkedRows.map(o => new OrganisationInvoiceGroupMember({ memberKlipOrganisationId: o.id }))
        };

        const _invoiceGroupChanged = (newOrganisationInvoiceGroup: OrganisationInvoiceGroup) => {

            resetForm({
                values: newOrganisationInvoiceGroup
            });

            allOrganisations.value.forEach(org => {
                if(newOrganisationInvoiceGroup.organisations?.some(o => o.memberKlipOrganisationId === org.id)) {
                    org.checked = true;
                    org.type = 'available';
                } else {
                    org.checked = false;
                }
            });


            createInvoiceGroupModalEvents.triggerModalToggle();
        }

        const createNewOrganisationInvoiceGroup = () => {
            _invoiceGroupChanged(OrganisationInvoiceGroup.fromJS({}));
        }

        const organisationInvoiceGroupToSave = computed(() => OrganisationInvoiceGroup.fromJS({
                ...props.invoiceGroup,
                name: name.value,
                email: email.value,
                attention: attention.value,
                reference: reference.value,
                invoiceContactName: invoiceContactName.value,
                invoiceContactEmail: invoiceContactEmail.value,
                invoiceContactPhoneNumber: invoiceContactPhoneNumber.value,
                organisations: organisations.value,
            })
        );

        const _saveOrganisationInvoiceGroup = async () => {
            isSending.value = true;

            emit('onSave', organisationInvoiceGroupToSave.value);

            createInvoiceGroupModalEvents.triggerModalToggle();
            resetForm();
        }

        const _loadOrganisations = (disableOrganisation: OrganisationInvoiceGroupMember[]) => {
            allOrganisations.value = props.subOrganisations.map(o => {
                return {
                    ...o,
                    type: disableOrganisation.some(org => org.memberKlipOrganisationId === o.id) ? 'processing' : 'available'
                } as OrganisationForInvoiceGroup;
            });
        }


        const _onInvalidSubmit = (submittedValues) => {
            console.log('_onInvalidSubmit', submittedValues);
        }
        const onSubmit = handleSubmit(_saveOrganisationInvoiceGroup, _onInvalidSubmit);

        watch(() => props.invoiceGroup, (newValue) => _invoiceGroupChanged(newValue), { immediate: true, deep: true });

        watch(() => props.disabledOrganisations, (newValue) => _loadOrganisations(newValue), { immediate: true, deep: true });
        watch(() => props.disableCreateButton, (newValue) => disableCreate.value = newValue, { immediate: true, deep: true });

        return {
            isSending,
            disableSubmit,
            disableCreate,
            organisationsMeta,
            isOrganisationsDataTableLoading,
            allOrganisations,
            organisationsColumns,
            title,
            submitButtonLabel,
            createNewOrganisationInvoiceGroup,
            cancel,
            onSubmit,
            isEditMode,
            checkboxChanged,

            email,
            attention,
            reference,
            name,
            invoiceContactName,
            invoiceContactEmail,
            invoiceContactPhoneNumber,

            fields,
        }
    }
})
